import * as React from "react";
import { cn } from "../cn";

export interface TabItem {
  value: string;
  label: string;
  count?: number;
}

export function Tabs({
  items,
  value,
  onValueChange,
  variant = "default",
  className,
}: {
  items: TabItem[];
  value: string;
  onValueChange: (v: string) => void;
  variant?: "default" | "pill";
  className?: string;
}) {
  if (variant === "pill") {
    return (
      <div
        className={cn("inline-flex max-w-full items-center gap-1 overflow-x-auto rounded-lg bg-subtle p-1", className)}
        role="tablist"
      >
        {items.map((t) => (
          <button
            key={t.value}
            role="tab"
            aria-selected={value === t.value}
            onClick={() => onValueChange(t.value)}
            className={cn(
              "focus-ring shrink-0 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors duration-150",
              value === t.value ? "bg-card text-ink shadow-card" : "text-mute hover:text-ink",
            )}
          >
            {t.label}
            {t.count !== undefined && <span className="ml-1.5 num text-faint">{t.count}</span>}
          </button>
        ))}
      </div>
    );
  }

  // Six-plus tabs (Sub-projects/Approvals/Evidence/Audit trail/Participants/
  // Analytics and similar strips) don't fit a phone-width viewport in one
  // row — overflow-x-auto + shrink-0/whitespace-nowrap on each tab turns
  // this into a horizontally-scrollable strip instead of clipping or
  // wrapping tab labels, matching the overflow-x-auto idiom already used
  // for MilestoneStrip/MilestoneStepper/Wizard's row-of-items UI.
  return (
    <div className={cn("flex items-center gap-6 overflow-x-auto border-b border-line", className)} role="tablist">
      {items.map((t) => (
        <button
          key={t.value}
          role="tab"
          aria-selected={value === t.value}
          onClick={() => onValueChange(t.value)}
          className={cn(
            "focus-ring -mb-px flex shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-0.5 pb-3 text-sm font-medium transition-colors duration-150",
            value === t.value
              ? "border-brand text-ink [box-shadow:inset_0_-2px_0_var(--accent-line)]"
              : "border-transparent text-mute hover:text-ink",
          )}
        >
          {t.label}
          {t.count !== undefined && (
            <span
              className={cn(
                "rounded-full px-1.5 text-[11px] leading-[18px] num",
                value === t.value ? "bg-brandSoft text-brand" : "bg-subtle text-faint",
              )}
            >
              {t.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}
